<script setup>
import XInputField from "@/components/inputs/XInputField.vue";
import {computed} from "vue";

// eslint-disable-next-line no-undef
const props = defineProps({
	id: String,
	label: String,
	name: String,
	title: String,
	required: Boolean,
	disabled: Boolean,
	errors: Array,
	text: String,
    value : {
        type : Boolean,
        default : false,
    },
	modelValue: Boolean,
});
// eslint-disable-next-line no-undef
const emit = defineEmits(['update:modelValue'])

const modelValueComputed = computed({
	get() {
		return props.modelValue;
	},
	set(val) {
		emit('update:modelValue', val);
        return val;
	},
})
</script>

<template>
	<XInputField :label="label" :errors="errors" :required="required" tag="span">
		<template v-if="$slots.label" #label="{ labelValue }">
			<slot name="label" :label-value="labelValue" />
		</template>

		<label
			class="input-check input-check--checkbox"
			:title="title"
			:class="[
				{ active: modelValue },
				{ error: errors?.length },
				{ disabled },
			]"
		>
			<input
				v-model="modelValueComputed"
				:id="id"
				type="checkbox"
				:disabled="disabled"
				:name="name"
				class="input-check__input"
			>
			<span class="input-check__checkmark input-check__checkmark--checkbox" />
			<span v-if="$slots.default" class="input-check__value">
				<slot></slot>
			</span>
			<span v-else class="input-check__value" v-html="text"></span>
		</label>

		<template v-if="$slots.action" #action>
			<slot name="action" />
		</template>
	</XInputField>
</template>

<style>
.input-check {
    --di-border-color-error: #ec4646;
	--checkmark-size: 16px;
	--checkmark-border-radius: 0;
	--checkmark-border-width: 2px;
	--checkmark-border-color: #121212;
	--checkmark-border-color-hover: #121212;
	--checkmark-border-color-active: #121212;
	--checkmark-border-color-disabled: #121212;
	--checkmark-bg: #fff;
	--checkmark-bg-hover: #fff;
	--checkmark-bg-active: #fff;
	--checkmark-bg-disabled: #fff;
	--checkmark-color: #121212;
	position: relative;
	display: flex;
	flex: 1 1 auto;
	width: 100%;
	cursor: pointer;
}
.input-check:hover .input-check__checkmark {
	border-color: var(--checkmark-border-color-hover);
	background-color: var(--checkmark-bg-hover);
}
.input-check.disabled {
	cursor: not-allowed;
}
.input-check.disabled .input-check__checkmark {
	border-color: var(--checkmark-border-color-disabled);
}
.input-check.disabled .input-check__value {
	color: var(--di-color-disabled);
}
.input-check.readonly:not(.disabled) {
	cursor: default;
}
.input-check.error .input-check__checkmark {
	border-color: var(--di-border-color-error);
}
.input-check.active {
	--checkmark-bg-hover: var(--color-primary-400);
}
.input-check.active:hover .input-check__checkmark {
	background-color: var(--checkmark-bg-hover);
}
.input-check.active.disabled .input-check__checkmark {
	background-color: var(--checkmark-bg-disabled);
}
.input-check.active .input-check__checkmark {
	border-color: var(--checkmark-border-color-active);
	background-color: var(--checkmark-bg-active);
}
.input-check.active .input-check__checkmark::before {
	opacity: 1;
}
.input-check__checkmark {
	display: flex;
	flex-shrink: 0;
	align-items: center;
	justify-content: center;
	width: var(--checkmark-size);
	height: var(--checkmark-size);
	margin-right: 12px;
	border-width: var(--checkmark-border-width);
	border-style: solid;
	border-radius: var(--checkmark-border-radius);
	background-color: var(--checkmark-bg);
	color: var(--checkmark-color);
	transition: border-color 0.3s ease, background-color 0.3s ease;
    border-color: var(--checkmark-border-color);
}
.input-check__checkmark::before {
	content: '';
	position: relative;
	flex-grow: 0;
	flex-shrink: 0;
	width: 100%;
	height: 100%;
	background-color: currentcolor;
	mask-position: center;
	mask-size: contain;
	opacity: 0;
	transition: opacity 0.3s ease;
	pointer-events: none;
}
.input-check__value {
	color: #bfbfbf;
	font-size: 12px;
	line-height: 1.33;
    user-select: none;
}
.input-check__value a {
	color:  #121212;
}
.input-check__input {
	position: absolute;
	top: 0;
	left: 0;
	width: var(--checkmark-size);
	height: var(--checkmark-size);
    max-width: var(--checkmark-size);
    max-height: var(--checkmark-size);
	opacity: 0;
	appearance: none;
}
.input-check__input:focus-visible:not(&:checked) + .input-check__checkmark {
	border-color: var(--checkmark-border-color-active);
	background-color: var(--checkmark-bg-active);
}
.input-check__input:checked:focus-visible + .input-check__checkmark {
	outline: 2px solid var(--di-bg-focus);
	outline-offset: 2px;
}
.input-check__checkmark--checkbox::before {
	mask-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='32' height='32' viewBox='0 0 24 24'%3E%3Cpath fill='white' d='m9.55 18l-5.7-5.7l1.425-1.425L9.55 15.15l9.175-9.175L20.15 7.4z'/%3E%3C/svg%3E")
}
.input-check--radio {
	--checkmark-border-radius: 50%;
}
</style>
